import { FormProps } from '@rjsf/core'
import { ErrorTransformer, RJSFSchema, UiSchema } from '@rjsf/utils'
import validator from '@rjsf/validator-ajv8'
import { cloneDeep, has, isEmpty } from 'lodash'
import { ReactNode, useEffect, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import ErrorComponent from '@/components/ErrorComponent'
import Form from '@/pages/SpaceAppBuilder/DataSourcesBuilderPage/components/Form'
import { useAppSelector } from '@/store/hooks'
import { getCurrentOrganization } from '@/store/selectors'
import cn from '@/utilities/cn'

interface WorkgridDatasourceFormProps {
  schema: RJSFSchema
  uiSchema?: UiSchema<any, RJSFSchema>
  initialFormData?: any
  onChange?: (formData: any) => void
  onSubmit?: (values: any) => void
  className?: string
  children?: ReactNode
  templates?: FormProps['templates']
  widgets?: FormProps['widgets']
  transformErrors?: ErrorTransformer
  disabled?: boolean
}

export const WorkgridDatasourceForm = ({
  schema,
  uiSchema,
  initialFormData,
  transformErrors,
  widgets,
  templates,
  onSubmit,
  onChange,
  className,
  children,
  disabled = false
}: WorkgridDatasourceFormProps) => {
  const [formData, setFormData] = useState(initialFormData || null)
  const [formSchema, setFormSchema] = useState<RJSFSchema>()
  const { companyCode } = useAppSelector(getCurrentOrganization)

  useEffect(() => {
    setFormData(initialFormData)
  }, [initialFormData])

  useEffect(() => {
    const fSchema = cloneDeep(schema)

    const injectAdditionalPropertiesEmptyRequired = (obj: RJSFSchema) => {
      if (!obj || typeof obj !== 'object') return

      for (const [key, value] of Object.entries(obj)) {
        if (value && typeof value === 'object') {
          injectAdditionalPropertiesEmptyRequired(value as RJSFSchema)
        } else if ((key === 'additionalProperties' && !value?.required) || key === 'required') {
          obj.required = []
        }
      }
    }
    // This has been added because for some unknown reason RJSF inject a required false into the additional props object when required property is omitted or undefined.
    // This has been added because for some unknown reason RJSF inject a required key which causes the schema to fail to compile.
    // With the lib code in the link below this caused the form to crash as you can't do an includes on a boolean value
    // https://github.com/rjsf-team/react-jsonschema-form/blob/7f54d45cafeb50864ffb5002f8f924860cb78942/packages/utils/src/schema/getDefaultFormState.ts#L292
    //
    injectAdditionalPropertiesEmptyRequired(fSchema)
    setFormSchema(fSchema)
  }, [schema])

  const handleChange = (event: any) => {
    onChange && onChange(event.formData)
    if (has(event, 'formData.callbackUrl') || has(event, 'formData.configuration.callbackUrl')) {
      const callbackUrl = `https://${companyCode}.workgrid.com/console/datasource/callback`
      if (isEmpty(event?.formData?.configuration)) {
        event.formData.callbackUrl = callbackUrl
      } else {
        event.formData.configuration.callbackUrl = callbackUrl
      }
    }
    setFormData(event.formData)
  }
  return (
    // @ts-expect-error
    <ErrorBoundary
      fallbackRender={({ error }) => (
        <div className="mx-auto max-w-2xl">
          <ErrorComponent errors={error ? [error] : []} />
        </div>
      )}
    >
      <Form
        templates={templates}
        widgets={widgets}
        className={cn(className, 'flex flex-shrink-0 flex-col gap-4 p-4')}
        validator={validator}
        schema={formSchema || {}}
        uiSchema={uiSchema}
        noHtml5Validate
        showErrorList={false}
        transformErrors={transformErrors}
        formData={formData}
        onChange={handleChange}
        onSubmit={onSubmit}
        disabled={disabled}
      >
        {children}
      </Form>
    </ErrorBoundary>
  )
}
